import type { TeeBox } from '@/lib/api/types';
import { useMenu } from '@/components/ui/useMenu';
import { cx } from '@/lib/cx';
import { teeColor } from '@/routes/record/teeColor';
import { t } from '@/lib/i18n';
import styles from './Pickers.module.css';
import { ChevronDownIcon } from '@/components/icons';

/**
 * The hole and tee pickers from canvas 1f — pill buttons with a caret, not native selects.
 *
 * A `<select>` was the first cut and it looked wrong on this screen: the platform draws its
 * own arrow, its own font, and on dark chrome its own background. These keep the pill
 * exactly as designed while staying keyboard-operable and announced as a listbox.
 */

interface MenuProps {
  /** Node rather than string: the tee picker puts a colour dot inside its label. */
  label: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

function Menu({ label, children, className }: MenuProps) {
  const menu = useMenu<HTMLDivElement>();

  return (
    <div className={cx(styles.root, className)} ref={menu.ref}>
      <button
        type="button"
        className={styles.pill}
        onClick={menu.toggle}
        aria-haspopup="listbox"
        aria-expanded={menu.open}
      >
        {label}
        <span className={styles.caret} aria-hidden="true">
          <ChevronDownIcon size={14} />
        </span>
      </button>
      {menu.open && (
        <div className={styles.menu} role="listbox" onClick={menu.close}>
          {children}
        </div>
      )}
    </div>
  );
}

export function HolePicker({
  holes,
  value,
  onChange,
}: {
  holes: number[];
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <Menu label={t('hole.number', { number: value })}>
      <div className={styles.holeGrid}>
        {holes.map((n) => (
          <button
            key={n}
            type="button"
            role="option"
            aria-selected={n === value}
            className={cx(styles.holeCell, n === value && styles.optionActive)}
            onClick={() => onChange(n)}
          >
            {n}
          </button>
        ))}
      </div>
    </Menu>
  );
}

export function TeePicker({
  teeBoxes,
  value,
  onChange,
  compact = false,
}: {
  teeBoxes: TeeBox[];
  value: string | null;
  onChange: (name: string) => void;
  compact?: boolean;
}) {
  const active = teeBoxes.find((t) => t.name === value);

  return (
    <Menu
      className={compact ? styles.compact : undefined}
      label={
        <>
          {t('hole.teePicker', { name: value ?? '—' })}
          {active && (
            <span
              className={styles.dot}
              style={{ background: teeColor(active) }}
              aria-hidden="true"
            />
          )}
        </>
      }
    >
      {teeBoxes.map((t) => (
        <button
          key={t.name}
          type="button"
          role="option"
          aria-selected={t.name === value}
          className={cx(styles.option, t.name === value && styles.optionActive)}
          onClick={() => onChange(t.name)}
        >
          <span className={styles.dot} style={{ background: teeColor(t) }} aria-hidden="true" />
          {t.name}
        </button>
      ))}
    </Menu>
  );
}
